import { Guides } from "./components/guides.js";
import { Shuffler } from "./components/shuffler.js";
import { OldBrowserChecker } from "./components/oldBrowserChecker.js";
import { CodeBlocks } from "./components/codeBlocks.js";
import { Howl } from "howler";

/* -------------------------------------------------- */
/* 🙂 General */
/* -------------------------------------------------- */

const isReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const isDesktop = () => {
  return window.matchMedia("(min-width: 1000px)").matches;
};

/* -------------------------------------------------- */
/* 👵 Check for old browsers */
/* -------------------------------------------------- */

new OldBrowserChecker();

/* -------------------------------------------------- */
/* 📏 Guides */
/* -------------------------------------------------- */

new Guides();

/* -------------------------------------------------- */
/* 🃏 Shuffler */
/* -------------------------------------------------- */

let shuffleElements = document.querySelectorAll("[data-shuffle-children]");

shuffleElements.forEach((element) => {
  new Shuffler(element);
});

/* -------------------------------------------------- */
/* 📋 Copy buttons */
/* -------------------------------------------------- */

new CodeBlocks();

/* -------------------------------------------------- */
/* 🗾 Doitsu Component */
/* -------------------------------------------------- */
class Doitsu {
  constructor() {
    this.initAudio();
    this.initDialog();
  }

  /* -------------------------------------------------- */
  /* 🎵 Audio */
  /* -------------------------------------------------- */
  initAudio() {
    this.currentlyPlaying = null;
    this.soundButtons = document.querySelectorAll(".doitsu__button--play");
    this.soundButtons.forEach((button) => this.setupAudioButton(button));
  }

  setupAudioButton(button) {
    if (!button.dataset.sound) {
      console.warn("Doitsu: Sound button missing data-sound attribute");
      return;
    }

    const sound = new Howl({
      src: [button.dataset.sound],
      html5: true,
    });

    button.addEventListener("click", () =>
      this.handleAudioClick(sound, button)
    );
    sound.on("end", () => this.handleAudioEnd(button));
  }

  handleAudioClick(sound, button) {
    if (sound.playing()) {
      this.stopAudio(sound, button);
    } else {
      this.playAudio(sound, button);
    }
  }

  playAudio(sound, button) {
    if (this.currentlyPlaying) {
      this.stopAudio(this.currentlyPlaying.sound, this.currentlyPlaying.button);
    }

    sound.play();
    button.setAttribute("aria-pressed", "true");
    this.currentlyPlaying = { sound, button };
  }

  stopAudio(sound, button) {
    sound.stop();
    button.setAttribute("aria-pressed", "false");
    this.currentlyPlaying = null;
  }

  handleAudioEnd(button) {
    button.setAttribute("aria-pressed", "false");
    this.currentlyPlaying = null;
  }

  /* -------------------------------------------------- */
  /* 💬 Dialog */
  /* -------------------------------------------------- */
  initDialog() {
    this.createDialog();
    this.bindDialogEvents();
    this.initInfoButtons();
  }

  createDialog() {
    this.dialog = document.createElement("dialog");
    this.dialog.className = "doitsu-dialog";

    const template = `
      <div class="doitsu-dialog__content text-wrap">
        <h2 class="doitsu-dialog__heading" lang="jp"></h2>
        <div class="doitsu-dialog__text"></div>
      </div>
      <button class="doitsu-dialog__close" aria-label="Close dialog">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    `;

    this.dialog.innerHTML = template;
    document.body.appendChild(this.dialog);

    this.dialogHeading = this.dialog.querySelector(".doitsu-dialog__heading");
    this.dialogText = this.dialog.querySelector(".doitsu-dialog__text");
    this.dialogCloseButton = this.dialog.querySelector(".doitsu-dialog__close");
    this.activeInfoButton = null;
  }

  bindDialogEvents() {
    this.dialogCloseButton.addEventListener("click", () => this.closeDialog());

    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) this.closeDialog();
    });

    this.dialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      this.closeDialog();
    });

    this.dialog.addEventListener("close", () => {
      if (this.activeInfoButton) {
        this.activeInfoButton.setAttribute("aria-pressed", "false");
        this.activeInfoButton = null;
      }
    });
  }

  initInfoButtons() {
    document.querySelectorAll(".doitsu__button--info").forEach((button) => {
      button.setAttribute("aria-pressed", "false");
      button.addEventListener("click", (e) => this.handleInfoButtonClick(e));
    });
  }

  handleInfoButtonClick(event) {
    const button = event.currentTarget;
    const doitsuElement = button.closest(".doitsu");

    if (!doitsuElement) {
      console.error("Doitsu: Info button not within .doitsu element");
      return;
    }

    const japaneseElement = doitsuElement.querySelector(".doitsu__japanese");
    if (!japaneseElement) {
      console.error("Doitsu: Japanese text element not found");
      return;
    }

    const info = button.dataset.text;
    if (!info) {
      console.error("Doitsu: Info text not found in data-text attribute");
      return;
    }

    this.activeInfoButton = button;
    button.setAttribute("aria-pressed", "true");
    this.showDialog(japaneseElement.textContent, this.decodeHTML(info));
  }

  decodeHTML(html) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  }

  showDialog(japanese, content) {
    this.dialogHeading.textContent = japanese;
    this.dialogText.innerHTML = content;
    this.dialog.showModal();
  }

  closeDialog() {
    this.dialog.close();
  }
}

/* -------------------------------------------------- */
/* 🚀 Initialize */
/* -------------------------------------------------- */
new Doitsu();
